import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile, FiChevronDown, FiChevronUp, FiCalendar, FiUser, FiClock } from "react-icons/fi";
import { GET_SALES_REPORT, GET_INVOICE_DETAILS, BASE_URL, GET_ITEM_MASTER } from '../../utlilities/config';
import axios from 'axios';
import { initializeWebUserInfo, getWebUserInfo } from '../../utlilities/webConfig';
import AsyncSelect from 'react-select/async';
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HighlightMetricsModal from './components/HighlightMetricsModal';
import SalesReportBillsTable from './components/SalesReportBillsTable';

import { FiAlertCircle } from "react-icons/fi";



// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
    const today = new Date();
    const todayFormatted = formatLocalDate(today);

    return filters.reduce((accumulator, filter) => {
        if (filter.type === 'date') {
            // Set both fromDate and toDate to today by default
            const dateValue = todayFormatted;
            return { ...accumulator, [filter.id]: dateValue };
        }

        if (filter.type === 'select') {
            const defaultValue = filter.options?.[0]?.value ?? '';
            return { ...accumulator, [filter.id]: defaultValue };
        }

        return { ...accumulator, [filter.id]: '' };
    }, {});
};

// Custom Select Styles
const customSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '42px',
        height: '42px',
        fontSize: '14px',
        borderRadius: '0.5rem',
        border: '1px solid #e2e8f0',
        boxShadow: 'none',
        '&:hover': {
            border: '1px solid #cbd5e1',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 12px',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '42px',
    }),
    menu: (base) => ({
        ...base,
        fontSize: '14px',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: 9999,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
        color: state.isSelected ? 'white' : '#374151',
        cursor: 'pointer',
        ':active': {
            backgroundColor: '#4f46e5',
            color: 'white',
        },
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

// Report configuration
const reportConfig = {
    filters: [
        { id: 'fromDate', type: 'date', label: 'From Date' },
        { id: 'toDate', type: 'date', label: 'To Date' },
        { id: 'itemId', type: 'item', label: 'Item' },
    ],
    table: {
        columns: [
            { key: 'InvoiceNo', label: 'Inv. No' },
            { key: 'EntryDate', label: 'Date' },
            { key: 'PatientName', label: 'Patient Name' },
            { key: 'DoctorName', label: 'Doctor Name' },
            { key: 'GrandTotal', label: 'G Total' },
            { key: 'TaxAmount', label: 'Tax Amount' },
            { key: 'DiscountAmount', label: 'Discount' },
            { key: 'TotalAmount', label: 'Total Amount' },
            { key: 'BalanceDue', label: 'Balance Due' },
        ],
    },
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesBillItemWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [salesData, setSalesData] = useState([]);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const [invoiceDetails, setInvoiceDetails] = useState({});
    const [expandedBills, setExpandedBills] = useState(new Set());
    const [loadingDetails, setLoadingDetails] = useState(new Set());
    const token = localStorage.getItem('accessToken');
    const [userInfo, setUserInfo] = useState({});
    const [itemsMaster, setItemsMaster] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const itemSearchDebounceRef = useRef(null);
    const [showExportModal, setShowExportModal] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);


    const isElectron = () => {
        return window && window.process && window.process.type;
    };

    let ipcRenderer;

    if (isElectron()) {
        ipcRenderer = window.require('electron').ipcRenderer;
    }


    useEffect(() => {
        const fetchData = async () => {
            let userinfo;

            if (isElectron()) {
                userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
                setUserInfo(userinfo);
            } else {
                initializeWebUserInfo();
                userinfo = getWebUserInfo().userInfo; // Fetch from webConfig
                setUserInfo(userinfo);
            }
        };

        fetchData();
    }, []);


    // Initial load - fetch first 1000 items
    useEffect(() => {
        const fetchItems = async () => {
            try {
                const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
                    headers: {
                        'XApiKey': process.env.REACT_APP_API_KEY,
                        'Authorization': `Bearer ${token}`,
                        'PageNo': 1,
                        'PageSize': 1000,
                        'FilterText': '',
                        'FilterColumn': '0',
                        'FilterValue': '0',
                    },
                });

                if (response.status === 200) {
                    const masterData = response.data?.data?.masterData || [];
                    const pharmacyItems = masterData.filter(
                        (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
                    );
                    setItemsMaster(pharmacyItems);
                }
            } catch (error) {
                console.error('Failed to fetch items:', error);
                toast.error('Failed to load items', {
                    icon: <FiAlertCircle className="text-red-500" />
                });
            }
        };

        if (token) {
            fetchItems();
        }
    }, [token]);

    // Load items from API based on search text (for AsyncSelect)
    const loadItemOptions = (inputValue, callback) => {
        // Clear previous debounce timeout
        if (itemSearchDebounceRef.current) {
            clearTimeout(itemSearchDebounceRef.current);
        }

        // If input is less than 2 characters, return initial items
        if (!inputValue || inputValue.length < 2) {
            const options = (itemsMaster || []).map((item) => ({
                value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
                label: item.Item_Name ?? item.ItemName ?? '',
                ...item,
            }));
            const filteredOptions = inputValue
                ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                : options;
            callback(filteredOptions);
            return;
        }

        // Debounce API call
        itemSearchDebounceRef.current = setTimeout(async () => {
            try {
                const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
                    headers: {
                        'XApiKey': process.env.REACT_APP_API_KEY,
                        'Authorization': `Bearer ${token}`,
                        'PageNo': 1,
                        'PageSize': 1000,
                        'FilterText': inputValue,
                        'FilterColumn': '0',
                        'FilterValue': '0',
                    },
                });

                if (response.status === 200) {
                    const masterData = response.data?.data?.masterData || [];
                    const pharmacyItems = masterData.filter(
                        (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
                    );

                    const options = pharmacyItems.map((item) => ({
                        value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
                        label: item.Item_Name ?? item.ItemName ?? '',
                        ...item,
                    }));

                    const filteredOptions = inputValue
                        ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                        : options;
                    callback(filteredOptions);
                } else {
                    callback([]);
                }
            } catch (error) {
                console.error('Failed to fetch items:', error);
                callback([]);
            }
        }, 300); // 300ms debounce
    };

    // Get selected item option for AsyncSelect value prop
    const getSelectedItemOption = (itemId) => {
        if (!itemId) return null;

        // First, check if selectedItem exists and matches (most reliable)
        if (selectedItem) {
            const selectedValue = selectedItem.Item_No ?? selectedItem.ItemNo ?? selectedItem.id ?? selectedItem.value;
            if (selectedValue === itemId || String(selectedValue) === String(itemId)) {
                return {
                    value: selectedValue,
                    label: selectedItem.Item_Name ?? selectedItem.ItemName ?? selectedItem.label ?? '',
                };
            }
        }

        // Fallback: search in itemsMaster
        const item = itemsMaster.find(i => {
            const itemNo = i.Item_No ?? i.ItemNo ?? i.id;
            return itemNo === itemId || String(itemNo) === String(itemId);
        });

        if (item) {
            return {
                value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
                label: item.Item_Name ?? item.ItemName ?? '',
            };
        }

        return null;
    };

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (itemSearchDebounceRef.current) {
                clearTimeout(itemSearchDebounceRef.current);
            }
        };
    }, []);


    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getSalesReport();
    };

    const handleExport = () => {
        if (salesData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = () => {
        try {
            // Prepare data for Excel
            const excelData = salesData.map((row) => {
                const totalAmount = parseFloat(row.TotalAmount || 0);
                const discountAmount = parseFloat(row.DiscountAmount || 0);
                const grandTotal = totalAmount + discountAmount;

                return {
                    'Invoice No': row.InvoiceNo,
                    'Date': new Date(row.EntryDate).toLocaleDateString('en-GB'),
                    'Patient Name': row.PatientName || '',
                    'Doctor Name': row.DoctorName || '-',
                    'Grand Total': Number(grandTotal.toFixed(2)),
                    'Tax Amount': Number(parseFloat(row.TaxAmount || 0).toFixed(2)),
                    'Discount': Number(parseFloat(row.DiscountAmount || 0).toFixed(2)),
                    'Total Amount': Number(totalAmount.toFixed(2)),
                    'Balance Due': Number(parseFloat(row.BalanceDue || 0).toFixed(2)),
                };
            });

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 12 }, // Invoice No
                { wch: 12 }, // Date
                { wch: 25 }, // Patient Name
                { wch: 20 }, // Doctor Name
                { wch: 15 }, // Grand Total
                { wch: 12 }, // Tax Amount
                { wch: 12 }, // Discount
                { wch: 15 }, // Total Amount
                { wch: 15 }, // Balance Due
            ];
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Sales Bill Item Wise');

            // Generate filename with date range
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Sales_Bill_Item_Wise_${fromDate}_to_${toDate}.xlsx`;

            // Write file
            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');

            // Add title
            doc.setFontSize(16);
            doc.text('Sales Bill Item Wise Report', 14, 15);

            // Add date range
            doc.setFontSize(10);
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 22);

            // Prepare table data
            const tableData = salesData.map((row) => {
                const totalAmount = parseFloat(row.TotalAmount || 0);
                const discountAmount = parseFloat(row.DiscountAmount || 0);
                const grandTotal = totalAmount + discountAmount;

                return [
                    row.InvoiceNo,
                    new Date(row.EntryDate).toLocaleDateString('en-GB'),
                    row.PatientName || '',
                    row.DoctorName || '-',
                    `${grandTotal.toFixed(2)}`,
                    `${parseFloat(row.TaxAmount || 0).toFixed(2)}`,
                    `${parseFloat(row.DiscountAmount || 0).toFixed(2)}`,
                    `${totalAmount.toFixed(2)}`,
                    `${parseFloat(row.BalanceDue || 0).toFixed(2)}`,
                ];
            });

            // Add table using autoTable function
            autoTable(doc, {
                head: [['Inv. No', 'Date', 'Patient Name', 'Doctor Name', 'G Total', 'Tax Amount', 'Discount', 'Total Amount', 'Balance Due']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 28, left: 14, right: 14 },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 40 },
                    3: { cellWidth: 35 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 25 },
                    7: { cellWidth: 25 },
                    8: { cellWidth: 25 },
                },
            });

            // Generate filename
            const filename = `Sales_Bill_Item_Wise_${fromDate.replace(/\//g, '-')}_to_${toDate.replace(/\//g, '-')}.pdf`;

            // Save PDF
            doc.save(filename);
            toast.success('PDF file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    // Pagination calculations
    const totalPages = Math.ceil(salesData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = salesData.slice(startIndex, endIndex);

    // Reset to page 1 when filters change or new data is loaded
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate, filters.itemId, selectedItem, salesData.length]);

    const getSalesReport = useCallback(async () => {
        setLoading(true);
        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;

            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const response = await axios.get(`${BASE_URL}${GET_SALES_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'Item',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'searchKey': filters.itemId || selectedItem?.value || 0,
                    'patientId': 0,
                    'itemNo': 0,
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by InvoiceNo in descending order
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || '';
                    const invoiceB = b.InvoiceNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setSalesData(sortedData);
                setLastRefreshedAt(new Date());
                // Clear expanded bills and details when new data is loaded
                setExpandedBills(new Set());
                setInvoiceDetails({});
                setLoadingDetails(new Set());
            } else {
                toast.error('Failed to fetch sales report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch sales report:', error);
            toast.error('Failed to fetch sales report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token, selectedItem]);

    // Toggle bill expansion and fetch details on demand
    const toggleBillExpansion = useCallback(async (row) => {
        const invoiceKey = `${row.Sequence}_${row.InvoiceNo}`;
        const isExpanded = expandedBills.has(invoiceKey);

        if (isExpanded) {
            // Collapse: just remove from expanded set
            setExpandedBills(prev => {
                const newSet = new Set(prev);
                newSet.delete(invoiceKey);
                return newSet;
            });
        } else {
            // Expand: add to expanded set and fetch details if not already loaded
            setExpandedBills(prev => new Set(prev).add(invoiceKey));

            // Only fetch if details are not already loaded
            if (!invoiceDetails[invoiceKey]) {
                setLoadingDetails(prev => new Set(prev).add(invoiceKey));
                try {
                    const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
                        headers: {
                            'XApiKey': process.env.REACT_APP_API_KEY,
                            'Authorization': `Bearer ${token}`,
                        },
                        params: {
                            Sequence: row.Sequence,
                            InvoiceNo: row.InvoiceNo.toString(),
                        }
                    });

                    if (response.status === 200) {
                        const invoiceData = response.data.data;
                        if (invoiceData && invoiceData.details) {
                            setInvoiceDetails(prev => ({
                                ...prev,
                                [invoiceKey]: invoiceData
                            }));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching invoice details:', error);
                    toast.error('Failed to load invoice details', { autoClose: 2000 });
                } finally {
                    setLoadingDetails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(invoiceKey);
                        return newSet;
                    });
                }
            }
        }
    }, [expandedBills, invoiceDetails, token]);


    // Handle item change
    const handleItemChange = (selectedOption) => {
        if (!selectedOption) {
            setSelectedItem(null);
            handleFilterChange('itemId', 0);
            return;
        }

        // Use selectedOption directly (it contains all item data from AsyncSelect)
        const item = selectedOption;

        // Add to itemsMaster if not already present (for getSelectedItemOption to work)
        if (item) {
            const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? item.value;
            const existingItem = itemsMaster.find(i => {
                const iNo = i.Item_No ?? i.ItemNo ?? i.id;
                return iNo === itemNo || String(iNo) === String(itemNo);
            });

            if (!existingItem) {
                // Add the full item object to itemsMaster
                setItemsMaster(prev => [...prev, item]);
            }
        }

        setSelectedItem(selectedOption);
        handleFilterChange('itemId', selectedOption.value);
    };


    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Sales Report
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Sales Bill Item Wise
                                    </h1>
                                </div>
                            )}
                            <form className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'select') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <select
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                >
                                                    {(filter.options ?? []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'item') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <div className="[&>div]:!h-auto">
                                                    <AsyncSelect
                                                        loadOptions={loadItemOptions}
                                                        isSearchable
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        defaultOptions={true}
                                                        cacheOptions={true}
                                                        value={getSelectedItemOption(selectedItem?.value || filters[filter.id])}
                                                        onChange={handleItemChange}
                                                        noOptionsMessage={({ inputValue }) =>
                                                            inputValue && inputValue.length < 2
                                                                ? 'Type at least 2 characters to search'
                                                                : 'No items found'
                                                        }
                                                        loadingMessage={() => 'Searching items...'}
                                                        styles={customSelectStyles}
                                                        placeholder="Select Item"
                                                        isClearable
                                                    />
                                                </div>
                                            </label>
                                        );
                                    }

                                    return null;
                                })}
                            </form>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                    setSelectedItem(null);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-5">
                                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Bills
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">{salesData.length}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Discount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.DiscountAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Grand Total
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || 0) + parseFloat(item.DiscountAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Pending
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.BalanceDue || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Cards Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, salesData.length)}</span> of <span className="font-semibold text-slate-700">{salesData.length}</span> bills for this report.
                                    </p>
                                    {/* Items per page selector */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-600">Items per page:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            {salesData.length > 0 ? (
                                <SalesReportBillsTable
                                    rows={paginatedData}
                                    startIndex={startIndex}
                                    expandedBills={expandedBills}
                                    invoiceDetails={invoiceDetails}
                                    loadingDetails={loadingDetails}
                                    onToggleExpand={toggleBillExpansion}
                                    formatInvoice={(row) => String(row.InvoiceNo ?? '')}
                                    userInfo={userInfo}
                                    showUserName={false}
                                />
                            ) : (
                                <div className="text-center py-12">
                                    <FiFileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                                    <p className="text-slate-500 text-lg">No data available for the selected period</p>
                                </div>
                            )}
                            {/* Bottom Pagination Controls */}
                            {salesData.length > 0 && totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-slate-200">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 7) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 6 + i;
                                            } else {
                                                pageNum = currentPage - 3 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                                        : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="text-sm text-slate-600 px-2">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(SalesBillItemWise);